import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  ClipboardList,
  DollarSign,
  Download,
  Layers,
  MapPin,
  Package,
  Printer,
  RefreshCcw,
  Search,
  Tag,
  Truck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { buildBrandOptions, normalizeBrandKey, normalizeBrandLabel } from '../utils/brandFilters';
import { fetchAllPaginated, getProductStockForSite } from '../utils/productCatalog';
import { persistSelectedSiteId, resolvePreferredSiteId } from '../utils/sitePreferences';

const DEFAULT_PEDIDO_SUGERIDO_PRINT_CONFIG = {
  headerText: 'Pedido sugerido',
  subHeaderText: 'Reposicion automatica segun stock minimo',
  footerText: 'Reporte interno generado desde Impulza.',
  showLogo: true,
  logoBase64: null,
  printerWidth: 'a4',
};

const money = (value) =>
  Number(value || 0).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const qty = (value) =>
  Number(value || 0).toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const csvEscape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const normalizeText = (value) => String(value ?? '').trim().toLowerCase();

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatDateLabel = (value, options = {}) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('es-AR', options);
};

const resolvePurchaseTimestamp = (purchase) => {
  const rawValue = purchase?.fecha || purchase?.created_at || null;
  if (!rawValue) return 0;
  const parsed = new Date(rawValue).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const resolveLastSupplierByProduct = (purchases, providersById = new Map()) => {
  const latestByProduct = {};
  const safePurchases = Array.isArray(purchases) ? [...purchases] : [];

  safePurchases.sort((left, right) => resolvePurchaseTimestamp(right) - resolvePurchaseTimestamp(left));

  safePurchases.forEach((purchase) => {
    if (normalizeText(purchase?.estado) === 'anulado') return;
    if (normalizeText(purchase?.tipo_comprobante) === 'pago') return;

    const items = Array.isArray(purchase?.items) ? purchase.items : [];
    const supplierId = purchase?.proveedor_id ? String(purchase.proveedor_id) : '';
    const supplierName =
      String(purchase?.proveedor_nombre || providersById.get(supplierId) || '').trim() || 'Proveedor sin nombre';
    const purchaseDate = purchase?.fecha || purchase?.created_at || null;

    items.forEach((item) => {
      const productId = String(item?.product_id || '').trim();
      if (!productId || latestByProduct[productId]) return;

      latestByProduct[productId] = {
        proveedorId: supplierId,
        proveedor: supplierName,
        fecha: purchaseDate,
      };
    });
  });

  return latestByProduct;
};

const resolvePedidoSugeridoPrintConfig = (organizationData) => ({
  ...DEFAULT_PEDIDO_SUGERIDO_PRINT_CONFIG,
  ...(organizationData?.print_config?.pedido_sugerido || {}),
});

const downloadCsv = (rows) => {
  if (!rows.length) {
    toast.error('No hay productos para exportar');
    return;
  }

  const headers = [
    'Producto',
    'Categoria',
    'Marca',
    'Tipo',
    'Ultimo proveedor',
    'Stock',
    'Costo',
    'Cantidad a reponer',
  ];

  const lines = [
    headers.map(csvEscape).join(';'),
    ...rows.map((row) =>
      [
        row.producto,
        row.categoria,
        row.marca,
        row.tipo,
        row.ultimoProveedor,
        qty(row.stock),
        Number(row.costo || 0).toFixed(2),
        qty(row.cantidadAReponer),
      ].map(csvEscape).join(';')
    ),
  ];

  const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const date = new Date().toISOString().slice(0, 10);

  link.href = url;
  link.download = `Pedido_Sugerido_${date}.csv`;
  link.click();
  URL.revokeObjectURL(url);
  toast.success('Pedido sugerido exportado');
};

const buildPedidoSugeridoPrintHtml = ({
  config,
  filters,
  generatedAt,
  organizationData,
  rows,
  selectedSiteName,
  stats,
}) => {
  const isA4 = config.printerWidth === 'a4';
  const pageSize = isA4 ? 'A4 portrait' : config.printerWidth === '58mm' ? '58mm auto' : '80mm auto';
  const pagePadding = isA4 ? '12mm' : '6mm';
  const logoHtml =
    config.showLogo && config.logoBase64
      ? `<img src="${config.logoBase64}" alt="Logo" class="logo" />`
      : '';
  const appliedFiltersHtml = filters.length
    ? filters
        .map(
          (filter) => `
            <div class="filter-chip">
              <span class="filter-label">${escapeHtml(filter.label)}:</span>
              <span class="filter-value">${escapeHtml(filter.value)}</span>
            </div>
          `
        )
        .join('')
    : '<div class="filter-chip"><span class="filter-label">Vista:</span><span class="filter-value">General</span></div>';

  const summaryHtml = `
    <div class="summary-grid">
      <div class="summary-card">
        <span class="summary-label">Productos</span>
        <strong>${rows.length}</strong>
      </div>
      <div class="summary-card">
        <span class="summary-label">Unidades</span>
        <strong>${escapeHtml(qty(stats.unidadesAReponer))}</strong>
      </div>
      <div class="summary-card">
        <span class="summary-label">Inversion</span>
        <strong>$ ${escapeHtml(money(stats.inversionEstimada))}</strong>
      </div>
    </div>
  `;

  const reportMetaHtml = `
    <div class="meta-row">
      <span>Generado: ${escapeHtml(formatDateLabel(generatedAt, { dateStyle: 'short', timeStyle: 'short' }))}</span>
      <span>Sucursal: ${escapeHtml(selectedSiteName)}</span>
      ${
        organizationData?.name
          ? `<span>Empresa: ${escapeHtml(organizationData.name)}</span>`
          : ''
      }
    </div>
  `;

  const rowsHtml = isA4
    ? `
      <table>
        <thead>
          <tr>
            <th>Producto</th>
            <th>Categoria</th>
            <th>Marca</th>
            <th>Tipo</th>
            <th>Ultimo proveedor</th>
            <th class="text-right">Stock</th>
            <th class="text-right">Costo</th>
            <th class="text-right">A reponer</th>
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (row) => `
                <tr>
                  <td>
                    <div class="product-name">${escapeHtml(row.producto)}</div>
                    <div class="product-meta">Minimo ${escapeHtml(qty(row.stockMinimo))}</div>
                  </td>
                  <td>${escapeHtml(row.categoria)}</td>
                  <td>${escapeHtml(row.marca)}</td>
                  <td>${escapeHtml(row.tipo)}</td>
                  <td>
                    <div>${escapeHtml(row.ultimoProveedor)}</div>
                    <div class="product-meta">Ult. compra ${escapeHtml(row.ultimaCompraFechaLabel)}</div>
                  </td>
                  <td class="text-right">${escapeHtml(qty(row.stock))}</td>
                  <td class="text-right">$ ${escapeHtml(money(row.costo))}</td>
                  <td class="text-right">${escapeHtml(qty(row.cantidadAReponer))}</td>
                </tr>
              `
            )
            .join('')}
        </tbody>
      </table>
    `
    : `
      <div class="compact-list">
        ${rows
          .map(
            (row) => `
              <div class="compact-card">
                <div class="compact-head">
                  <strong>${escapeHtml(row.producto)}</strong>
                  <span class="compact-badge">${escapeHtml(qty(row.cantidadAReponer))}</span>
                </div>
                <div class="compact-meta">${escapeHtml(row.categoria)} | ${escapeHtml(row.marca)} | ${escapeHtml(row.tipo)}</div>
                <div class="compact-meta">Proveedor: ${escapeHtml(row.ultimoProveedor)}</div>
                <div class="compact-meta">Ult. compra: ${escapeHtml(row.ultimaCompraFechaLabel)}</div>
                <div class="compact-grid">
                  <span>Stock ${escapeHtml(qty(row.stock))}</span>
                  <span>Costo $ ${escapeHtml(money(row.costo))}</span>
                </div>
              </div>
            `
          )
          .join('')}
      </div>
    `;

  const footerHtml = config.footerText
    ? `<div class="footer-text">${escapeHtml(config.footerText)}</div>`
    : '';

  return `
    <!doctype html>
    <html lang="es">
      <head>
        <meta charset="utf-8" />
        <title>Pedido sugerido</title>
        <style>
          @page {
            size: ${pageSize};
            margin: ${pagePadding};
          }
          * {
            box-sizing: border-box;
          }
          body {
            margin: 0;
            font-family: Arial, sans-serif;
            color: #0f172a;
            background: #ffffff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .report {
            width: 100%;
          }
          .header {
            display: flex;
            justify-content: space-between;
            gap: 16px;
            align-items: flex-start;
            border-bottom: 2px solid #0b2a36;
            padding-bottom: 12px;
            margin-bottom: 12px;
          }
          .header-copy {
            flex: 1;
            min-width: 0;
          }
          .logo {
            max-width: 120px;
            max-height: 72px;
            object-fit: contain;
          }
          .title {
            margin: 0;
            font-size: ${isA4 ? '24px' : '16px'};
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.08em;
          }
          .subtitle {
            margin-top: 6px;
            font-size: ${isA4 ? '12px' : '10px'};
            color: #475569;
            white-space: pre-wrap;
          }
          .meta-row {
            display: flex;
            flex-wrap: wrap;
            gap: 8px 16px;
            font-size: 11px;
            color: #475569;
            margin-bottom: 12px;
          }
          .filters {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 12px;
          }
          .filter-chip {
            border: 1px solid #cbd5e1;
            border-radius: 999px;
            padding: 6px 10px;
            font-size: 10px;
            background: #f8fafc;
          }
          .filter-label {
            font-weight: 700;
            color: #475569;
            margin-right: 4px;
            text-transform: uppercase;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(${isA4 ? 3 : 1}, minmax(0, 1fr));
            gap: 10px;
            margin-bottom: 14px;
          }
          .summary-card {
            border: 1px solid #cbd5e1;
            border-radius: 12px;
            padding: 12px;
            background: #f8fafc;
          }
          .summary-label {
            display: block;
            font-size: 10px;
            font-weight: 700;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            margin-bottom: 6px;
          }
          .summary-card strong {
            font-size: ${isA4 ? '20px' : '14px'};
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          thead th {
            padding: 10px 8px;
            background: #0b2a36;
            color: #ffffff;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            text-align: left;
          }
          tbody td {
            padding: 10px 8px;
            border-bottom: 1px solid #e2e8f0;
            font-size: 11px;
            vertical-align: top;
          }
          .text-right {
            text-align: right;
          }
          .product-name {
            font-weight: 700;
          }
          .product-meta {
            margin-top: 4px;
            font-size: 10px;
            color: #64748b;
          }
          .compact-list {
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          .compact-card {
            border: 1px solid #cbd5e1;
            border-radius: 12px;
            padding: 10px;
            background: #f8fafc;
          }
          .compact-head {
            display: flex;
            justify-content: space-between;
            gap: 8px;
            align-items: center;
            margin-bottom: 6px;
          }
          .compact-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 48px;
            padding: 3px 8px;
            border-radius: 999px;
            background: #dcfce7;
            color: #166534;
            font-weight: 700;
            font-size: 11px;
          }
          .compact-meta {
            font-size: 10px;
            color: #475569;
            margin-bottom: 4px;
          }
          .compact-grid {
            display: flex;
            justify-content: space-between;
            gap: 8px;
            margin-top: 8px;
            font-size: 10px;
            font-weight: 700;
          }
          .footer-text {
            margin-top: 16px;
            padding-top: 10px;
            border-top: 1px solid #cbd5e1;
            font-size: 10px;
            color: #475569;
            white-space: pre-wrap;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <main class="report">
          <header class="header">
            <div class="header-copy">
              <h1 class="title">${escapeHtml(config.headerText || 'Pedido sugerido')}</h1>
              ${
                config.subHeaderText
                  ? `<div class="subtitle">${escapeHtml(config.subHeaderText)}</div>`
                  : ''
              }
            </div>
            ${logoHtml}
          </header>
          ${reportMetaHtml}
          <section class="filters">${appliedFiltersHtml}</section>
          ${summaryHtml}
          ${rowsHtml}
          ${footerHtml}
        </main>
        <script>
          window.onload = function () {
            window.focus();
            window.print();
          };
        </script>
      </body>
    </html>
  `;
};

const resolveUnitCost = (product) => {
  const productCost = Number(product?.cost_price || 0);
  if (productCost > 0) return productCost;

  const variants = Array.isArray(product?.product_variants) ? product.product_variants : [];
  const firstVariantWithCost = variants.find((variant) => Number(variant?.cost_price || 0) > 0);
  return Number(firstVariantWithCost?.cost_price || 0);
};

const resolveProductType = (product) => {
  const rawType = String(product?.product_type || '').trim();
  return rawType || 'Producto terminado';
};

function PedidoSugerido({ onNavigate }) {
  const { profile, organization } = useAuth();

  const [productos, setProductos] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [organizationData, setOrganizationData] = useState(null);
  const [lastSupplierByProduct, setLastSupplierByProduct] = useState({});
  const [loading, setLoading] = useState(true);

  const [busqueda, setBusqueda] = useState('');
  const [filtroSitio, setFiltroSitio] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [filtroMarca, setFiltroMarca] = useState('todas');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroProveedor, setFiltroProveedor] = useState('todos');

  useEffect(() => {
    if (!profile?.organization_id) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  useEffect(() => {
    if (filtroSitio) persistSelectedSiteId(filtroSitio);
  }, [filtroSitio]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const orgId = profile.organization_id;

      const [sitesRes, providersRes, organizationRes, productsRes, purchasesRes] = await Promise.all([
        supabase
          .from('sitios')
          .select('id, nombre, is_active')
          .eq('organization_id', orgId)
          .order('nombre', { ascending: true }),
        supabase
          .from('proveedores')
          .select('id, razon_social')
          .eq('organization_id', orgId)
          .order('razon_social', { ascending: true }),
        // organization viene del AuthContext (un fetch por sesión).
        Promise.resolve({ data: organization, error: null }),
        fetchAllPaginated(() =>
          supabase
            .from('products')
            .select(`
              id,
              name,
              brand,
              product_type,
              min_stock,
              cost_price,
              active,
              product_categories(name),
              stock_levels(quantity, site_id),
              product_variants(cost_price)
            `)
            .eq('organization_id', orgId)
            .not('active', 'is', false)
            .not('is_active', 'is', false)
            .is('archived_at', null)
            .order('name', { ascending: true })
        ),
        fetchAllPaginated(() =>
          supabase
            .from('compras')
            .select('id, proveedor_id, proveedor_nombre, fecha, created_at, estado, tipo_comprobante, items')
            .eq('organization_id', orgId)
            .order('fecha', { ascending: false })
        ),
      ]);

      if (sitesRes.error) throw sitesRes.error;
      if (providersRes.error) throw providersRes.error;
      if (organizationRes.error) throw organizationRes.error;

      const availableSites = (sitesRes.data || []).filter((site) => site?.is_active !== false);
      const providersById = new Map(
        (providersRes.data || []).map((provider) => [String(provider.id), provider?.razon_social || 'Proveedor sin nombre'])
      );

      setSitios(availableSites);
      setProductos(productsRes || []);
      setOrganizationData(organizationRes.data || null);
      setLastSupplierByProduct(resolveLastSupplierByProduct(purchasesRes || [], providersById));
      setFiltroSitio((current) =>
        resolvePreferredSiteId(availableSites, {
          currentSiteId: current,
        })
      );
    } catch (error) {
      console.error('Error cargando pedido sugerido:', error);
      toast.error('No se pudo armar el pedido sugerido');
    } finally {
      setLoading(false);
    }
  };

  const selectedSiteName = useMemo(() => {
    if (!filtroSitio) return 'Consolidado';
    return sitios.find((site) => String(site.id) === String(filtroSitio))?.nombre || 'Consolidado';
  }, [filtroSitio, sitios]);

  const enrichedRows = useMemo(() => {
    const siteId = filtroSitio || null;

    return (productos || [])
      .map((product) => {
        const minStock = Number(product?.min_stock || 0);
        const stock = Number(getProductStockForSite(product, siteId) || 0);
        const cantidadAReponer = Math.max(minStock - stock, 0);
        const categoria = product?.product_categories?.name || 'Sin categoria';
        const marca = normalizeBrandLabel(product?.brand);
        const tipo = resolveProductType(product);
        const costo = resolveUnitCost(product);
        const supplierInfo = lastSupplierByProduct[String(product.id)] || null;

        return {
          id: product.id,
          producto: product?.name || 'Sin nombre',
          categoria,
          marca,
          marcaKey: normalizeBrandKey(product?.brand),
          tipo,
          stock,
          costo,
          stockMinimo: minStock,
          cantidadAReponer,
          inversionEstimada: cantidadAReponer * costo,
          ultimoProveedorId: supplierInfo?.proveedorId || '',
          ultimoProveedor: supplierInfo?.proveedor || 'Sin compras',
          ultimaCompraFecha: supplierInfo?.fecha || null,
          ultimaCompraFechaLabel: supplierInfo?.fecha
            ? formatDateLabel(supplierInfo.fecha, { dateStyle: 'short' })
            : 'Sin historial',
        };
      })
      .filter((row) => row.stockMinimo > 0)
      .filter((row) => row.cantidadAReponer > 0)
      .sort((left, right) => {
        const leftGap = left.stock - left.stockMinimo;
        const rightGap = right.stock - right.stockMinimo;

        if (leftGap !== rightGap) return leftGap - rightGap;
        return left.producto.localeCompare(right.producto, 'es', { sensitivity: 'base' });
      });
  }, [filtroSitio, lastSupplierByProduct, productos]);

  const categoryOptions = useMemo(
    () => ['todas', ...new Set(enrichedRows.map((row) => row.categoria))],
    [enrichedRows]
  );

  const brandOptions = useMemo(
    () => buildBrandOptions(enrichedRows, (row) => row.marca, 'Sin marca'),
    [enrichedRows]
  );

  const typeOptions = useMemo(
    () => ['todos', ...new Set(enrichedRows.map((row) => row.tipo))],
    [enrichedRows]
  );

  const providerOptions = useMemo(() => {
    const optionsMap = new Map();

    enrichedRows.forEach((row) => {
      const optionValue = row.ultimoProveedorId || 'sin-proveedor';
      if (!optionsMap.has(optionValue)) {
        optionsMap.set(optionValue, {
          value: optionValue,
          label: row.ultimoProveedor || 'Sin compras',
        });
      }
    });

    return [
      { value: 'todos', label: 'TODOS' },
      ...Array.from(optionsMap.values()).sort((left, right) =>
        left.label.localeCompare(right.label, 'es', { sensitivity: 'base' })
      ),
    ];
  }, [enrichedRows]);

  const rows = useMemo(() => {
    const search = busqueda.trim().toLowerCase();

    return enrichedRows.filter((row) => {
      if (filtroCategoria !== 'todas' && row.categoria !== filtroCategoria) return false;
      if (filtroMarca !== 'todas' && row.marcaKey !== filtroMarca) return false;
      if (filtroTipo !== 'todos' && row.tipo !== filtroTipo) return false;

      const providerKey = row.ultimoProveedorId || 'sin-proveedor';
      if (filtroProveedor !== 'todos' && providerKey !== filtroProveedor) return false;

      if (!search) return true;

      return [row.producto, row.categoria, row.marca, row.tipo, row.ultimoProveedor].some((value) =>
        String(value || '').toLowerCase().includes(search)
      );
    });
  }, [busqueda, enrichedRows, filtroCategoria, filtroMarca, filtroProveedor, filtroTipo]);

  const stats = useMemo(() => {
    const productosConPedido = rows.length;
    const unidadesAReponer = rows.reduce((acc, row) => acc + Number(row.cantidadAReponer || 0), 0);
    const inversionEstimada = rows.reduce((acc, row) => acc + Number(row.inversionEstimada || 0), 0);

    return { productosConPedido, unidadesAReponer, inversionEstimada };
  }, [rows]);

  const handlePrintReport = () => {
    if (!rows.length) {
      toast.error('No hay productos para imprimir');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1100,height=800');

    if (!printWindow) {
      toast.error('El navegador bloqueo la ventana de impresion');
      return;
    }

    const config = resolvePedidoSugeridoPrintConfig(organizationData);
    const filters = [
      { label: 'Sucursal', value: selectedSiteName },
      filtroCategoria !== 'todas' ? { label: 'Categoria', value: filtroCategoria } : null,
      filtroMarca !== 'todas'
        ? {
            label: 'Marca',
            value: brandOptions.find((option) => option.value === filtroMarca)?.label || filtroMarca,
          }
        : null,
      filtroTipo !== 'todos' ? { label: 'Tipo', value: filtroTipo } : null,
      filtroProveedor !== 'todos'
        ? {
            label: 'Proveedor',
            value: providerOptions.find((option) => option.value === filtroProveedor)?.label || 'Sin compras',
          }
        : null,
      busqueda.trim() ? { label: 'Busqueda', value: busqueda.trim() } : null,
    ].filter(Boolean);

    const html = buildPedidoSugeridoPrintHtml({
      config,
      filters,
      generatedAt: new Date(),
      organizationData,
      rows,
      selectedSiteName,
      stats,
    });

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  if (loading && productos.length === 0) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={40} />
        <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">
          Armando pedido sugerido...
        </p>
      </div>
    );
  }

  return (
    <div className="w-full p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 px-1">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate('compras')}
            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
            title="Volver a compras"
          >
            <ArrowLeft size={18} />
          </button>

          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <ClipboardList size={18} className="text-[#3FF2AA]" />
          </div>

          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">
              Pedido sugerido
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Reposicion automatica segun stock minimo cargado
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={fetchData}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 hover:text-[#51A7FD] hover:border-[#51A7FD]/30 transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <RefreshCcw size={15} /> Actualizar
          </button>

          <button
            onClick={handlePrintReport}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 hover:text-[#0B2A36] hover:border-[#0B2A36]/20 transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <Printer size={15} /> Imprimir reporte
          </button>

          <button
            onClick={() => downloadCsv(rows)}
            className="px-5 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <Download size={16} /> Exportar CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Productos a pedir</p>
            <Package size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{stats.productosConPedido}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">SKUs bajo minimo</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Unidades a reponer</p>
            <Layers size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-emerald-600 tracking-tight">{qty(stats.unidadesAReponer)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Cantidad sugerida</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Inversion estimada</p>
            <DollarSign size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-amber-600 tracking-tight">$ {money(stats.inversionEstimada)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Costo de reposicion</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 md:p-4 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-2.5">
          <div>
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Search size={12} /> Buscar
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
              <input
                type="text"
                value={busqueda}
                onChange={(event) => setBusqueda(event.target.value)}
                className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner"
                placeholder="PRODUCTO, MARCA, PROVEEDOR..."
              />
            </div>
          </div>

          <div>
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <MapPin size={12} /> Sucursal
            </label>
            <select
              value={filtroSitio}
              onChange={(event) => setFiltroSitio(event.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              <option value="">CONSOLIDADO</option>
              {sitios.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Tag size={12} /> Categoria
            </label>
            <select
              value={filtroCategoria}
              onChange={(event) => setFiltroCategoria(event.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {category === 'todas' ? 'TODAS' : category}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Package size={12} /> Marca
            </label>
            <select
              value={filtroMarca}
              onChange={(event) => setFiltroMarca(event.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              <option value="todas">TODAS</option>
              {brandOptions.map((brand) => (
                <option key={brand.value} value={brand.value}>
                  {brand.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Truck size={12} /> Proveedor
            </label>
            <select
              value={filtroProveedor}
              onChange={(event) => setFiltroProveedor(event.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              {providerOptions.map((provider) => (
                <option key={provider.value} value={provider.value}>
                  {provider.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Layers size={12} /> Tipo
            </label>
            <select
              value={filtroTipo}
              onChange={(event) => setFiltroTipo(event.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              {typeOptions.map((type) => (
                <option key={type} value={type}>
                  {type === 'todos' ? 'TODOS' : type}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-h-[500px] mb-10">
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
              Listado sugerido ({rows.length})
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[1320px]">
            <thead>
              <tr className="bg-slate-50/60 text-slate-400 border-b border-slate-100 text-[9px] font-black uppercase tracking-[0.2em]">
                <th className="px-6 py-4">Producto</th>
                <th className="px-6 py-4">Categoria</th>
                <th className="px-6 py-4">Marca</th>
                <th className="px-6 py-4">Tipo</th>
                <th className="px-6 py-4">Ultimo proveedor</th>
                <th className="px-6 py-4 text-right">Stock</th>
                <th className="px-6 py-4 text-right">Costo</th>
                <th className="px-6 py-4 text-right">Cantidad a reponer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 font-sans">
              {rows.map((row) => (
                <tr key={row.id} className="transition-all hover:bg-blue-50/20 group">
                  <td className="px-6 py-4">
                    <button
                      type="button"
                      onClick={() => onNavigate('producto-detalle', row.id)}
                      className="text-left"
                    >
                      <p className="font-black text-slate-700 text-[11px] uppercase tracking-tight group-hover:text-[#51A7FD] transition-colors">
                        {row.producto}
                      </p>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                        Minimo {qty(row.stockMinimo)}
                      </p>
                    </button>
                  </td>
                  <td className="px-6 py-4 text-[10px] font-bold text-slate-600 uppercase">{row.categoria}</td>
                  <td className="px-6 py-4 text-[10px] font-bold text-slate-600 uppercase">{row.marca}</td>
                  <td className="px-6 py-4 text-[10px] font-bold text-slate-600 uppercase">{row.tipo}</td>
                  <td className="px-6 py-4">
                    <p className="text-[10px] font-black text-slate-700 uppercase">{row.ultimoProveedor}</p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                      Ult. compra {row.ultimaCompraFechaLabel}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="inline-flex items-center justify-center min-w-[72px] px-2.5 py-1 rounded-lg border border-amber-100 bg-amber-50 text-amber-700 text-[10px] font-black">
                      {qty(row.stock)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-[11px] font-black text-slate-700">
                    $ {money(row.costo)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="inline-flex items-center justify-center min-w-[88px] px-2.5 py-1 rounded-lg border border-emerald-100 bg-emerald-50 text-emerald-700 text-[10px] font-black">
                      {qty(row.cantidadAReponer)}
                    </span>
                  </td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td colSpan="8" className="py-20 text-center">
                    <div className="flex flex-col items-center gap-2 text-slate-300">
                      <Package size={42} />
                      <p className="text-[10px] font-black uppercase tracking-widest">
                        No hay productos con reposicion pendiente
                      </p>
                      <p className="text-[10px] font-bold text-slate-400">
                        Revisa filtros o confirma que los productos tengan stock minimo configurado.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PedidoSugerido;
